import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import useDocumentTitle from "@/lib/useDocumentTitle";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Home as HomeIcon, Trees, MapPin, Phone, Mail, Plus, Pencil } from "lucide-react";

const CATEGORIES = [
  { key: "all", label: "All" },
  { key: "home_sale", label: "Homes for sale", icon: HomeIcon },
  { key: "home_wanted", label: "Homes wanted", icon: HomeIcon },
  { key: "land_sale", label: "Land for sale", icon: Trees },
  { key: "land_wanted", label: "Land wanted", icon: Trees },
];

export default function Listings() {
  useDocumentTitle(
    "Homes & Land Marketplace — Azure Gypsy Guild Members",
    "Browse and post homes for sale, homes wanted, land for sale, and land wanted on the Azure Gypsy Guild private members marketplace.",
  );
  const { user } = useAuth();
  const [tab, setTab] = useState("all");
  const [listings, setListings] = useState([]);

  useEffect(() => {
    const q = tab === "all" ? "" : `?listing_type=${tab}`;
    api.get(`/listings${q}`).then((r) => setListings(r.data)).catch(() => {});
  }, [tab]);

  return (
    <div className="min-h-screen bg-paper" data-testid="listings-page">
      <Navbar />
      <main className="max-w-7xl mx-auto px-4 py-12">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <span className="uppercase text-xs tracking-[0.25em] text-[color:var(--bright-green)]">Members Marketplace</span>
            <h1 className="font-serif text-4xl sm:text-5xl mt-2 leading-none">Homes & Land</h1>
          </div>
          <Button asChild className="bg-[color:var(--fire-red)] hover:bg-[color:var(--fire-red-hover)] text-white" data-testid="listings-new-btn">
            <Link to="/listings/new"><Plus className="mr-2 h-4 w-4" />Add a listing</Link>
          </Button>
        </div>

        <Tabs value={tab} onValueChange={setTab} className="mt-8">
          <div className="overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0">
            <TabsList data-testid="listings-tabs" className="w-max">
              {CATEGORIES.map((c) => (
                <TabsTrigger key={c.key} value={c.key} data-testid={`listings-tab-${c.key}`}>{c.label}</TabsTrigger>
              ))}
            </TabsList>
          </div>
          <TabsContent value={tab}>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-6" data-testid="listings-grid">
              {listings.map((l) => (
                <Card key={l.listing_id} className="border-black/5 hover:-translate-y-1 transition-all">
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                      <Badge className="bg-[color:var(--orange)] text-white hover:bg-[color:var(--orange-hover)] capitalize">
                        {l.listing_type.replace("_", " ")}
                      </Badge>
                      {l.price ? <span className="font-serif text-2xl">${Number(l.price).toLocaleString()}</span> : null}
                    </div>
                    <CardTitle className="font-serif text-2xl mt-2">{l.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-[color:var(--text-secondary)] flex items-start gap-1">
                      <MapPin className="h-4 w-4 mt-0.5 flex-shrink-0" />
                      {l.address}, {l.city}, {l.state} {l.zip_code}
                    </p>
                    <div className="flex flex-wrap gap-2 text-xs mt-3">
                      {l.bedrooms ? <Badge variant="outline" className="border-black/10">{l.bedrooms} bd</Badge> : null}
                      {l.bathrooms ? <Badge variant="outline" className="border-black/10">{l.bathrooms} ba</Badge> : null}
                      {l.sqft ? <Badge variant="outline" className="border-black/10">{l.sqft} sqft</Badge> : null}
                      {l.acreage ? <Badge variant="outline" className="border-black/10">{l.acreage} acres</Badge> : null}
                    </div>
                    <p className="text-sm mt-3 line-clamp-3">{l.description}</p>
                    <div className="mt-4 pt-4 border-t border-black/5 text-xs space-y-1 text-[color:var(--text-secondary)]">
                      <div className="flex items-center gap-1 break-all"><Mail className="h-3 w-3 flex-shrink-0" />{l.contact_email}</div>
                      {l.contact_phone && <div className="flex items-center gap-1"><Phone className="h-3 w-3" />{l.contact_phone}</div>}
                    </div>
                    {user && l.user_id === user.user_id && (
                      <div className="mt-3">
                        <Button asChild size="sm" variant="outline" className="border-black/10" data-testid={`listing-edit-${l.listing_id}`}>
                          <Link to={`/listings/${l.listing_id}/edit`}><Pencil className="h-3.5 w-3.5 mr-1" />Edit my listing</Link>
                        </Button>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
              {listings.length === 0 && (
                <p className="text-sm text-[color:var(--text-secondary)] col-span-full">No listings yet in this category.</p>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </main>
      <Footer />
    </div>
  );
}
